import type { BillingModuleKey } from '@bill-alps/backend/features/companies/modules';

/**
 * Fiduciaire (trustee) pricing — pure logic, importable by the app like
 * `planLimits.ts`.
 *
 * Per MODULE per mandate, degressive: each billed module has its own Stripe
 * volume-tiered price whose quantity is the number of mandates the trustee
 * pays that module for. The tier applies to the whole quantity of THAT module
 * (native Stripe `tiers_mode: 'volume'`), independently of the other modules.
 * No free mandates: an unpaid mandate/module simply runs on Starter limits.
 */
export interface FiduciaireModuleTier {
  /** Inclusive upper bound of the tier; null = unbounded last tier. */
  upTo: number | null;
  /** CHF per mandate per month for the module, excl. VAT. */
  ratePerMandate: number;
}

export const FIDUCIAIRE_MODULE_TIERS: readonly FiduciaireModuleTier[] = [
  { upTo: 9, ratePerMandate: 7 },
  { upTo: 24, ratePerMandate: 5 },
  { upTo: 49, ratePerMandate: 4 },
  { upTo: null, ratePerMandate: 3 },
];

/** Round a CHF amount to Rappen (0.01) — never bill floating-point dust. */
export function roundChf(amount: number): number {
  return Math.round(amount * 100) / 100;
}

/** CHF per mandate per month for one module at the given billed quantity. */
export function fiduciaireModuleRate(quantity: number): number {
  for (const tier of FIDUCIAIRE_MODULE_TIERS) {
    if (tier.upTo === null || quantity <= tier.upTo) {
      return tier.ratePerMandate;
    }
  }
  // Unreachable: the last tier is unbounded.
  return FIDUCIAIRE_MODULE_TIERS[FIDUCIAIRE_MODULE_TIERS.length - 1]
    .ratePerMandate;
}

/** Monthly CHF for one module: its own tier applied to its own quantity. */
export function fiduciaireModuleMonthly(quantity: number): number {
  if (quantity <= 0) return 0;
  return roundChf(fiduciaireModuleRate(quantity) * quantity);
}

/** Monthly CHF across modules — each module tiers on its own quantity. */
export function fiduciaireMonthlyTotal(
  quantitiesByModule: Partial<Record<BillingModuleKey, number>>,
): number {
  return roundChf(
    Object.values(quantitiesByModule).reduce(
      (total, quantity) => total + fiduciaireModuleMonthly(quantity ?? 0),
      0,
    ),
  );
}

// ---- Overage ----------------------------------------------------------------

/**
 * Metered features billed per act beyond a monthly allowance, on
 * fiduciaire-paid mandates only — never a hard block. Counted per mandate per
 * calendar month in `mandateUsage`, pushed as Stripe invoice items on the
 * subscription-cycle invoice.
 */
export type FiduciaireUsageFeature =
  'payslips' | 'supplierBillExtractions' | 'aiPrefills' | 'aiCategorizations';

export const FIDUCIAIRE_USAGE_FEATURES: readonly FiduciaireUsageFeature[] = [
  'payslips',
  'supplierBillExtractions',
  'aiPrefills',
  'aiCategorizations',
];

export interface FiduciaireOverageConfig {
  /** Units included per mandate per calendar month. */
  includedPerMonth: number;
  /** CHF per unit beyond the allowance, excl. VAT. */
  chfPerExtra: number;
}

export const FIDUCIAIRE_OVERAGE: Record<
  FiduciaireUsageFeature,
  FiduciaireOverageConfig
> = {
  payslips: { includedPerMonth: 20, chfPerExtra: 2 },
  supplierBillExtractions: { includedPerMonth: 200, chfPerExtra: 0.5 },
  aiPrefills: { includedPerMonth: 100, chfPerExtra: 0.2 },
  aiCategorizations: { includedPerMonth: 300, chfPerExtra: 0.1 },
};

export interface FiduciaireOverageResult {
  /** Units covered by the monthly allowance. */
  included: number;
  /** Units beyond the allowance — what gets billed. */
  overCount: number;
  /** CHF excl. VAT for the extra units, rounded to Rappen. */
  amountChf: number;
}

export function fiduciaireOverage(
  feature: FiduciaireUsageFeature,
  used: number,
): FiduciaireOverageResult {
  const { includedPerMonth, chfPerExtra } = FIDUCIAIRE_OVERAGE[feature];
  const overCount = Math.max(0, Math.floor(used) - includedPerMonth);
  return {
    included: includedPerMonth,
    overCount,
    amountChf: roundChf(overCount * chfPerExtra),
  };
}

export function isFiduciaireSubscriptionActive(
  fiduciaire: { stripeSubscriptionId?: string } | null | undefined,
): boolean {
  return !!fiduciaire?.stripeSubscriptionId;
}
